import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Area, AreaChart
} from 'recharts';
import { format, subDays } from 'date-fns';
import { formatIST, fmtMd, fmtMdHm, fmtFull, todayIST, daysAgoIST } from '../utils/time';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

// ─────────────────────────────────────────────────────────────────────────────
// ALL AVAILABLE KPI DEFINITIONS
// modal: { fetch } — function that returns a promise of { title, columns, rows }
// ─────────────────────────────────────────────────────────────────────────────
const ALL_KPIS = [
  // Patrol
  { id: 'total_patrols', category: 'Patrol', icon: '🚶', label: 'Total Patrols', color: '#4361ee', gradient: 'linear-gradient(135deg,#4361ee,#7b8ff8)',
    getValue: s => ({ value: n(s.total_patrols), sub: `Last period` }),
    modal: { title: 'All Patrols', fetchFn: (f,t) => fetchPatrols({},f,t) } },
  { id: 'completed_patrols', category: 'Patrol', icon: '✅', label: 'Completed', color: '#22c55e', gradient: 'linear-gradient(135deg,#22c55e,#86efac)',
    getValue: s => ({ value: n(s.completed_patrols), sub: `${pc(s.completed_patrols, s.total_patrols)}% rate` }),
    modal: { title: 'Completed Patrols', fetchFn: (f,t) => fetchPatrols({status:'completed'},f,t) } },
  { id: 'active_patrols', category: 'Patrol', icon: '🔄', label: 'Active Now', color: '#f59e0b', gradient: 'linear-gradient(135deg,#f59e0b,#fcd34d)',
    getValue: s => ({ value: n(s.active_patrols), sub: 'Currently running' }),
    modal: { title: 'Active Patrols', fetchFn: (f,t) => fetchPatrols({status:'active'},f,t) } },
  { id: 'abandoned_patrols', category: 'Patrol', icon: '🛑', label: 'Abandoned', color: '#ef233c', gradient: 'linear-gradient(135deg,#ef233c,#f87171)',
    getValue: s => ({ value: n(s.abandoned_patrols), sub: 'Force stopped / timed out' }),
    modal: { title: 'Abandoned Patrols', fetchFn: (f,t) => fetchPatrols({status:'abandoned'},f,t) } },
  { id: 'avg_compliance', category: 'Patrol', icon: '📊', label: 'Avg Compliance', color: '#dynamic', gradient: null,
    getValue: s => { const v = n(s.avg_compliance); const c = v >= 80 ? '#22c55e' : v >= 60 ? '#f59e0b' : '#ef233c'; return { value: `${v}%`, sub: `${n(s.high_compliance_count)} patrols ≥80%`, color: c, gradient: `linear-gradient(135deg,${c},${c}88)` }; },
    modal: { title: 'Compliance Breakdown', fetchFn: (f,t) => fetchPatrols({status:'completed'},f,t) } },
  { id: 'avg_duration', category: 'Patrol', icon: '⏱️', label: 'Avg Duration', color: '#8b5cf6', gradient: 'linear-gradient(135deg,#8b5cf6,#c4b5fd)',
    getValue: s => ({ value: s.avg_duration_mins ? `${s.avg_duration_mins}m` : '—', sub: 'Per completed patrol' }),
    modal: { title: 'Patrol Durations', fetchFn: (f,t) => fetchPatrols({status:'completed'},f,t) } },
  { id: 'low_compliance', category: 'Patrol', icon: '📉', label: 'Low Compliance', color: '#dynamic', gradient: null,
    getValue: s => { const v = n(s.low_compliance_count); const c = v > 0 ? '#ef233c' : '#22c55e'; return { value: v, sub: 'Patrols below 80%', color: c, gradient: `linear-gradient(135deg,${c},${c}88)` }; },
    modal: { title: 'Low Compliance Patrols', fetchFn: (f,t) => fetchPatrols({status:'completed',lowCompliance:true},f,t) } },
  // Checkpoints
  { id: 'cps_scanned', category: 'Checkpoints', icon: '📍', label: 'CPs Scanned', color: '#4361ee', gradient: 'linear-gradient(135deg,#4361ee,#7b8ff8)',
    getValue: s => ({ value: n(s.total_checkpoints_scanned), sub: `of ${n(s.total_checkpoints_expected)} expected` }),
    modal: { title: 'Checkpoint Scans', fetchFn: (f,t) => fetchScans({status:'verified'},f,t) } },
  { id: 'cps_missed', category: 'Checkpoints', icon: '⛔', label: 'CPs Missed', color: '#dynamic', gradient: null,
    getValue: s => { const v = Math.max(0, n(s.total_checkpoints_expected) - n(s.total_checkpoints_scanned)); const c = v > 0 ? '#ef233c' : '#22c55e'; return { value: v, sub: `${pc(s.total_checkpoints_scanned, s.total_checkpoints_expected)}% scan rate`, color: c, gradient: `linear-gradient(135deg,${c},${c}88)` }; },
    modal: { title: 'Missed / Out-of-Range Scans', fetchFn: (f,t) => fetchScans({status:'out_of_range'},f,t) } },
  // Incidents
  { id: 'total_incidents', category: 'Incidents', icon: '⚠️', label: 'Total Incidents', color: '#f59e0b', gradient: 'linear-gradient(135deg,#f59e0b,#fcd34d)',
    getValue: s => ({ value: n(s.total_incidents), sub: 'All severity levels' }),
    modal: { title: 'All Incidents', fetchFn: (f,t) => fetchIncidents({},f,t) } },
  { id: 'critical_incidents', category: 'Incidents', icon: '🚨', label: 'Critical', color: '#ef233c', gradient: 'linear-gradient(135deg,#ef233c,#f87171)',
    getValue: s => ({ value: n(s.critical_incidents), sub: `${n(s.high_incidents)} high severity` }),
    modal: { title: 'Critical Incidents', fetchFn: (f,t) => fetchIncidents({severity:'critical'},f,t) } },
  { id: 'medium_incidents', category: 'Incidents', icon: '🟠', label: 'Medium Incidents', color: '#f59e0b', gradient: 'linear-gradient(135deg,#f59e0b,#fcd34d)',
    getValue: s => ({ value: n(s.medium_incidents), sub: `${n(s.low_incidents)} low severity` }),
    modal: { title: 'Medium Incidents', fetchFn: (f,t) => fetchIncidents({severity:'medium'},f,t) } },
  // Guards
  { id: 'total_guards', category: 'Guards', icon: '👮', label: 'Total Guards', color: '#0f3460', gradient: 'linear-gradient(135deg,#0f3460,#1a6fa0)',
    getValue: s => ({ value: n(s.guard_count), sub: `${n(s.supervisor_count)} supervisors` }),
    modal: { title: 'All Guards & Supervisors', fetchFn: () => fetchGuards({}) } },
  { id: 'active_guards', category: 'Guards', icon: '🟢', label: 'Active Guards', color: '#22c55e', gradient: 'linear-gradient(135deg,#22c55e,#86efac)',
    getValue: s => ({ value: n(s.active_guards), sub: 'In current period' }),
    modal: { title: 'Active Guards', fetchFn: () => fetchGuards({active:true}) } },
  // Shifts
  { id: 'total_shifts', category: 'Shifts', icon: '📅', label: 'Total Shifts', color: '#4361ee', gradient: 'linear-gradient(135deg,#4361ee,#7b8ff8)',
    getValue: s => ({ value: n(s.total_shifts), sub: `${n(s.upcoming_shifts)} upcoming` }),
    modal: { title: 'All Shifts', fetchFn: (f,t) => fetchShifts({},f,t) } },
  { id: 'completed_shifts', category: 'Shifts', icon: '✔️', label: 'Shifts Done', color: '#22c55e', gradient: 'linear-gradient(135deg,#22c55e,#86efac)',
    getValue: s => ({ value: n(s.completed_shifts), sub: `of ${n(s.total_shifts)} scheduled` }),
    modal: { title: 'Completed Shifts', fetchFn: (f,t) => fetchShifts({status:'completed'},f,t) } },
  { id: 'missed_shifts', category: 'Shifts', icon: '❌', label: 'Shifts Missed', color: '#dynamic', gradient: null,
    getValue: s => { const v = n(s.missed_shifts); const c = v > 0 ? '#ef233c' : '#22c55e'; return { value: v, sub: 'No-show shifts', color: c, gradient: `linear-gradient(135deg,${c},${c}88)` }; },
    modal: { title: 'Missed Shifts', fetchFn: (f,t) => fetchShifts({status:'missed'},f,t) } },
  { id: 'upcoming_shifts', category: 'Shifts', icon: '🗓️', label: 'Upcoming Shifts', color: '#8b5cf6', gradient: 'linear-gradient(135deg,#8b5cf6,#c4b5fd)',
    getValue: s => ({ value: n(s.upcoming_shifts), sub: 'Scheduled ahead' }),
    modal: { title: 'Upcoming Shifts', fetchFn: (f,t) => fetchShifts({status:'scheduled'},f,t) } },
  // Sites & Geo
  { id: 'total_sites', category: 'Sites', icon: '🏢', label: 'Total Sites', color: '#4361ee', gradient: 'linear-gradient(135deg,#4361ee,#7b8ff8)',
    getValue: s => ({ value: n(s.total_sites), sub: 'Registered sites' }),
    modal: { title: 'All Sites', fetchFn: () => fetchSites() } },
  { id: 'geo_alerts', category: 'Geofencing', icon: '🛡️', label: 'Geo Alerts', color: '#dynamic', gradient: null,
    getValue: s => { const v = n(s.total_geo_alerts); const u = n(s.unacknowledged_alerts); const c = u > 0 ? '#ef233c' : '#22c55e'; return { value: v, sub: `${u} unacknowledged`, color: c, gradient: `linear-gradient(135deg,${c},${c}88)` }; },
    modal: { title: 'Geofence Alerts', fetchFn: (f,t) => fetchGeoAlerts(f,t) } },
];

const DEFAULT_KPI_IDS = [
  'total_patrols','completed_patrols','active_patrols','abandoned_patrols','avg_compliance','avg_duration',
  'cps_scanned','cps_missed','total_incidents','critical_incidents','total_guards','active_guards',
  'total_shifts','missed_shifts','total_sites','geo_alerts','low_compliance',
];

function n(v) { return Number(v) || 0; }
function pc(a, b) { return n(b) > 0 ? Math.round(n(a) / n(b) * 100) : 0; }
const SEV = { low: '#22c55e', medium: '#f59e0b', high: '#ef233c', critical: '#7c3aed' };

// ─────────────────────────────────────────────────────────────────────────────
// DATA FETCH HELPERS
// ─────────────────────────────────────────────────────────────────────────────
async function fetchPatrols({ status, lowCompliance } = {}, from, to) {
  const { data } = await axios.get('/api/patrols');
  let rows = data;
  if (status) rows = rows.filter(r => r.status === status);
  if (from)   rows = rows.filter(r => r.started_at >= from);
  if (to)     rows = rows.filter(r => r.started_at <= to + 'T23:59:59');
  if (lowCompliance) rows = rows.filter(r => parseFloat(r.compliance_percent) < 80);
  return {
    columns: [
      { key: 'guard_name',          label: 'Guard' },
      { key: 'route_name',          label: 'Route' },
      { key: 'site_name',           label: 'Site' },
      { key: 'status',              label: 'Status', badge: true },
      { key: 'compliance_percent',  label: 'Compliance', compliance: true },
      { key: 'completed_checkpoints', label: 'CPs Done' },
      { key: 'total_checkpoints',   label: 'CPs Total' },
      { key: 'duration_minutes',    label: 'Duration (min)' },
      { key: 'started_at',          label: 'Started', date: true },
    ],
    rows,
  };
}

async function fetchIncidents({ severity } = {}, from, to) {
  const { data } = await axios.get('/api/incidents');
  let rows = data;
  if (severity) rows = rows.filter(r => r.severity === severity);
  if (from) rows = rows.filter(r => r.reported_at >= from);
  if (to)   rows = rows.filter(r => r.reported_at <= to + 'T23:59:59');
  return {
    columns: [
      { key: 'title',       label: 'Title' },
      { key: 'guard_name',  label: 'Guard' },
      { key: 'severity',    label: 'Severity', badge: true, sevColor: true },
      { key: 'reported_at', label: 'Reported', date: true },
      { key: 'description', label: 'Description', truncate: true },
    ],
    rows,
  };
}

async function fetchGuards({ active } = {}) {
  const { data } = await axios.get('/api/users/guards');
  let rows = data;
  if (active) rows = rows.filter(r => r.is_active);
  return {
    columns: [
      { key: 'name',          label: 'Name' },
      { key: 'email',         label: 'Email' },
      { key: 'phone',         label: 'Phone' },
      { key: 'role',          label: 'Role', badge: true },
      { key: 'is_active',     label: 'Status', activeStatus: true },
      { key: 'created_at',    label: 'Joined', date: true },
    ],
    rows,
  };
}

async function fetchShifts({ status } = {}, from, to) {
  const { data } = await axios.get('/api/shifts');
  let rows = data;
  if (status) rows = rows.filter(r => r.status === status);
  if (from)   rows = rows.filter(r => r.shift_date >= from);
  if (to)     rows = rows.filter(r => r.shift_date <= to);
  return {
    columns: [
      { key: 'guard_name',  label: 'Guard' },
      { key: 'route_name',  label: 'Route' },
      { key: 'shift_date',  label: 'Date' },
      { key: 'start_time',  label: 'Start' },
      { key: 'end_time',    label: 'End' },
      { key: 'status',      label: 'Status', badge: true },
    ],
    rows,
  };
}

async function fetchSites() {
  const { data } = await axios.get('/api/sites');
  return {
    columns: [
      { key: 'name',      label: 'Site Name' },
      { key: 'address',   label: 'Address' },
      { key: 'latitude',  label: 'Lat' },
      { key: 'longitude', label: 'Lng' },
      { key: 'created_at', label: 'Created', date: true },
    ],
    rows: data,
  };
}

async function fetchScans({ status } = {}, from, to) {
  const { data } = await axios.get('/api/scans');
  let rows = Array.isArray(data) ? data : data.rows || [];
  if (status) rows = rows.filter(r => r.status === status);
  return {
    columns: [
      { key: 'guard_name',      label: 'Guard' },
      { key: 'checkpoint_name', label: 'Checkpoint' },
      { key: 'status',          label: 'Status', badge: true },
      { key: 'distance_from_checkpoint', label: 'Distance (m)' },
      { key: 'scanned_at',      label: 'Scanned At', date: true },
      { key: 'notes',           label: 'Notes', truncate: true },
    ],
    rows,
  };
}

async function fetchGeoAlerts(from, to) {
  const { data } = await axios.get('/api/geofencing/alerts');
  let rows = Array.isArray(data) ? data : data.rows || [];
  if (from) rows = rows.filter(r => r.created_at >= from);
  if (to)   rows = rows.filter(r => r.created_at <= to + 'T23:59:59');
  return {
    columns: [
      { key: 'guard_name',  label: 'Guard' },
      { key: 'zone_name',   label: 'Zone' },
      { key: 'alert_type',  label: 'Type', badge: true },
      { key: 'message',     label: 'Message', truncate: true },
      { key: 'acknowledged', label: 'Ack', activeStatus: true },
      { key: 'created_at',  label: 'Time', date: true },
    ],
    rows,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// CUSTOM TOOLTIP for charts
// ─────────────────────────────────────────────────────────────────────────────
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: 'rgba(15,20,50,0.92)', backdropFilter: 'blur(8px)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 10, padding: '10px 14px', fontSize: 12, color: 'white',
      boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
    }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: '#c7d2fe' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
          <span style={{ color: '#d1d5db' }}>{p.name}:</span>
          <span style={{ fontWeight: 700, color: 'white' }}>{p.value}</span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KPI DRILL-DOWN MODAL
// ─────────────────────────────────────────────────────────────────────────────
function KpiDrillModal({ drill, onClose }) {
  const { kpi, rows, columns, loading, error } = drill;
  const { value, color: dynColor, gradient: dynGradient } = kpi.getValue(drill.summary || {});
  const color    = dynColor    || kpi.color;
  const gradient = dynGradient || kpi.gradient || `linear-gradient(135deg,${color},${color}88)`;
  const [search, setSearch] = useState('');

  const filtered = rows.filter(r =>
    !search || Object.values(r).some(v => String(v||'').toLowerCase().includes(search.toLowerCase()))
  );

  const renderCell = (col, row) => {
    const val = row[col.key];

    if (col.date && val) {
      try { return <span style={{ color: '#64748b', fontSize: 11 }}>{formatIST(val, 'dd MMM yy, HH:mm')}</span>; }
      catch { return val; }
    }

    if (col.compliance) {
      const c = parseFloat(val) || 0;
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ width: 50, height: 5, borderRadius: 3, background: '#f1f5f9', overflow: 'hidden' }}>
            <div style={{ width: `${Math.min(c, 100)}%`, height: '100%', borderRadius: 3,
              background: c >= 80 ? '#22c55e' : c >= 60 ? '#f59e0b' : '#ef233c' }} />
          </div>
          <span style={{ fontWeight: 700, fontSize: 12,
            color: c >= 80 ? '#16a34a' : c >= 60 ? '#d97706' : '#dc2626' }}>{c}%</span>
        </div>
      );
    }

    if (col.badge) {
      const badgeColors = {
        completed: { bg: '#dcfce7', color: '#166534' },
        active:    { bg: '#fef3c7', color: '#92400e' },
        abandoned: { bg: '#fee2e2', color: '#991b1b' },
        scheduled: { bg: '#dbeafe', color: '#1e40af' },
        missed:    { bg: '#fee2e2', color: '#991b1b' },
        critical:  { bg: '#fce7f3', color: '#9d174d' },
        high:      { bg: '#fee2e2', color: '#991b1b' },
        medium:    { bg: '#fef3c7', color: '#92400e' },
        low:       { bg: '#dcfce7', color: '#166534' },
        admin:     { bg: '#ede9fe', color: '#5b21b6' },
        supervisor:{ bg: '#dbeafe', color: '#1e40af' },
        guard:     { bg: '#f0fdf4', color: '#166534' },
        entered:   { bg: '#fef3c7', color: '#92400e' },
        exited:    { bg: '#fee2e2', color: '#991b1b' },
      };
      const s = badgeColors[val] || { bg: '#f3f4f6', color: '#374151' };
      return (
        <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 700,
          background: s.bg, color: s.color, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
          {val}
        </span>
      );
    }

    if (col.activeStatus) {
      const active = val === 1 || val === true || val === '1';
      return <span style={{ fontSize: 12, color: active ? '#16a34a' : '#6b7280' }}>{active ? '✅ Active' : '⚪ Inactive'}</span>;
    }

    if (col.truncate && val && String(val).length > 40) {
      return <span title={val} style={{ fontSize: 12, color: '#64748b' }}>{String(val).slice(0, 40)}…</span>;
    }

    return <span style={{ fontSize: 13 }}>{val ?? '—'}</span>;
  };

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,20,50,0.55)',
        backdropFilter: 'blur(4px)',
        zIndex: 1100,
      }} />

      {/* Modal */}
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 'min(92vw, 860px)',
        maxHeight: '85vh',
        background: 'white',
        borderRadius: 20,
        boxShadow: '0 24px 80px rgba(0,0,0,0.25)',
        zIndex: 1101,
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          background: gradient,
          padding: '18px 22px',
          display: 'flex', alignItems: 'center', gap: 14, flexShrink: 0,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12,
            background: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, flexShrink: 0,
          }}>{kpi.icon}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: 'white' }}>{kpi.modal.title}</div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 2 }}>
              {loading ? 'Loading…' : `${filtered.length} record${filtered.length !== 1 ? 's' : ''}`}
              {search && ` matching "${search}"`}
            </div>
          </div>
          {/* Big value badge */}
          <div style={{
            background: 'rgba(255,255,255,0.2)', borderRadius: 12,
            padding: '6px 14px', textAlign: 'center',
          }}>
            <div style={{ fontSize: 24, fontWeight: 900, color: 'white', lineHeight: 1 }}>{value ?? '—'}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.7)', marginTop: 2 }}>Total</div>
          </div>
          <button onClick={onClose} style={{
            background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
            color: 'white', fontSize: 18, cursor: 'pointer',
            width: 34, height: 34, borderRadius: 9, lineHeight: 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>✕</button>
        </div>

        {/* Search bar */}
        {!loading && rows.length > 0 && (
          <div style={{ padding: '12px 22px', borderBottom: '1px solid #f0f2f5', flexShrink: 0 }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="🔍  Search records…"
              style={{
                width: '100%', padding: '9px 14px', borderRadius: 10,
                border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none',
                background: '#f8faff',
              }}
            />
          </div>
        )}

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
          {loading && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', gap: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: gradient, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                {kpi.icon}
              </div>
              <div style={{ color: '#64748b', fontSize: 14, fontWeight: 500 }}>Loading data…</div>
            </div>
          )}

          {error && (
            <div style={{ padding: 24, textAlign: 'center', color: '#ef233c', fontSize: 14 }}>
              ⚠️ {error}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div style={{ padding: '48px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📭</div>
              <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: 6 }}>
                {search ? 'No results match your search' : 'No records found'}
              </div>
              <div style={{ color: '#94a3b8', fontSize: 13 }}>
                {search ? 'Try a different search term' : 'There is no data for this KPI in the selected period'}
              </div>
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {columns.map(col => (
                    <th key={col.key} style={{
                      padding: '10px 16px', textAlign: 'left',
                      background: '#f8faff', borderBottom: '2px solid #e8ecf4',
                      fontSize: 11, fontWeight: 700, color: '#64748b',
                      textTransform: 'uppercase', letterSpacing: .5,
                      whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1,
                    }}>{col.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8faff'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                    {columns.map(col => (
                      <td key={col.key} style={{ padding: '10px 16px', verticalAlign: 'middle' }}>
                        {renderCell(col, row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        {!loading && filtered.length > 0 && (
          <div style={{
            padding: '10px 22px', borderTop: '1px solid #f0f2f5',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexShrink: 0, background: '#fafbff',
          }}>
            <span style={{ fontSize: 12, color: '#94a3b8' }}>
              Showing {filtered.length} of {rows.length} records
            </span>
            <button onClick={onClose} className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 16px' }}>
              Close
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KPI CARD — redesigned, now clickable
// ─────────────────────────────────────────────────────────────────────────────
function KpiCard({ kpi, summary, dragging, dragHandleProps, onClick }) {
  const { value, sub, color: dynColor, gradient: dynGradient } = kpi.getValue(summary);
  const color    = dynColor    || kpi.color;
  const gradient = dynGradient || kpi.gradient || `linear-gradient(135deg,${color},${color}88)`;
  const [hovered, setHovered] = useState(false);

  const inner = (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? 'linear-gradient(145deg, #ffffff, #f8faff)' : 'white',
        borderRadius: 16,
        padding: '18px 18px 16px',
        border: `1px solid ${hovered ? color + '40' : '#e8ecf4'}`,
        height: 118,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        boxShadow: dragging
          ? '0 16px 40px rgba(0,0,0,.18)'
          : hovered
          ? `0 8px 24px ${color}25, 0 2px 8px rgba(0,0,0,.06)`
          : '0 2px 8px rgba(0,0,0,.05)',
        opacity: dragging ? 0.85 : 1,
        transition: 'all .2s ease',
        position: 'relative', overflow: 'hidden',
        userSelect: 'none',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      {/* Accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: gradient, borderRadius: '16px 16px 0 0',
      }} />

      {/* Drag handle */}
      <div {...dragHandleProps} onClick={e => e.stopPropagation()} style={{
        position: 'absolute', top: 10, left: 9, cursor: 'grab',
        color: '#d1d5db', fontSize: 13, lineHeight: 1,
        opacity: hovered ? 1 : 0.5, transition: 'opacity .2s',
        zIndex: 2,
      }} title="Drag to reorder">⠿</div>

      {/* Click hint */}
      {onClick && hovered && (
        <div style={{
          position: 'absolute', bottom: 6, right: 8,
          fontSize: 9, color: color, fontWeight: 700, opacity: 0.7,
          letterSpacing: .3,
        }}>tap for details ›</div>
      )}

      {/* Icon + Label */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingLeft: 20 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .6, paddingTop: 2 }}>
          {kpi.label}
        </div>
        <div style={{
          width: 34, height: 34, borderRadius: 10, flexShrink: 0,
          background: gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, boxShadow: `0 4px 12px ${color}40`,
        }}>{kpi.icon}</div>
      </div>

      {/* Value */}
      <div style={{
        fontSize: 30, fontWeight: 800, color,
        lineHeight: 1, paddingLeft: 20,
        textShadow: `0 2px 8px ${color}30`,
      }}>{value ?? '—'}</div>

      {/* Sub */}
      <div style={{
        fontSize: 11, color: '#94a3b8',
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingLeft: 20,
      }}>{sub || '\u00a0'}</div>
    </div>
  );

  // All cards now clickable via onClick — no more <Link> wrapper
  return inner;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG PANEL
// ─────────────────────────────────────────────────────────────────────────────
function ConfigPanel({ visibleIds, onToggle, onReorder, onClose, onReset, onSave, saving }) {
  const cats = [...new Set(ALL_KPIS.map(k => k.category))];
  const dragItem = useRef(null);
  const dragOver = useRef(null);

  const handleDragStart = (id) => { dragItem.current = id; };
  const handleDragEnter = (id) => { dragOver.current = id; };
  const handleDragEnd   = () => {
    if (!dragItem.current || !dragOver.current || dragItem.current === dragOver.current) return;
    const list = [...visibleIds];
    const from = list.indexOf(dragItem.current);
    const to   = list.indexOf(dragOver.current);
    if (from < 0 || to < 0) return;
    list.splice(to, 0, list.splice(from, 1)[0]);
    onReorder(list);
    dragItem.current = null;
    dragOver.current = null;
  };

  return (
    <div className="dash-config-panel" style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 390,
      background: 'white', boxShadow: '-8px 0 40px rgba(0,0,0,.15)',
      zIndex: 1000, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 22px', borderBottom: '1px solid #f0f4ff',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0,
      }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'white' }}>⚙️ Customize Dashboard</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>Select and reorder your KPI cards</div>
        </div>
        <button onClick={onClose} style={{
          background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
          fontSize: 16, cursor: 'pointer', color: 'white', padding: '6px 10px', borderRadius: 8,
          lineHeight: 1, transition: 'background .15s',
        }}>✕</button>
      </div>

      {/* Active cards */}
      <div style={{ padding: '16px 22px', borderBottom: '1px solid #f0f4ff', flexShrink: 0 }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 10 }}>
          Active Cards ({visibleIds.length}) — drag to reorder
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto' }}>
          {visibleIds.map(id => {
            const kpi = ALL_KPIS.find(k => k.id === id);
            if (!kpi) return null;
            return (
              <div key={id}
                draggable
                onDragStart={() => handleDragStart(id)}
                onDragEnter={() => handleDragEnter(id)}
                onDragEnd={handleDragEnd}
                onDragOver={e => e.preventDefault()}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', borderRadius: 10, border: '1px solid #e8ecf4',
                  background: '#fafbff', cursor: 'grab', userSelect: 'none',
                  transition: 'box-shadow .15s',
                }}
              >
                <span style={{ color: '#cbd5e1', fontSize: 16 }}>⠿</span>
                <span style={{ fontSize: 15 }}>{kpi.icon}</span>
                <span style={{ fontSize: 13, flex: 1, fontWeight: 500, color: '#1e293b' }}>{kpi.label}</span>
                <span style={{ fontSize: 10, color: '#4361ee', background: '#eef2ff', borderRadius: 6, padding: '2px 7px', fontWeight: 600 }}>{kpi.category}</span>
                <button onClick={() => onToggle(id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef233c', fontSize: 15, padding: '0 2px', lineHeight: 1 }}
                  title="Remove">✕</button>
              </div>
            );
          })}
          {visibleIds.length === 0 && (
            <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 14 }}>No cards selected</div>
          )}
        </div>
      </div>

      {/* Available by category */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px 22px' }}>
        <div style={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 12 }}>
          Available Cards
        </div>
        {cats.map(cat => (
          <div key={cat} style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 10.5, fontWeight: 700, color: '#4361ee', textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8 }}>
              {cat}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ALL_KPIS.filter(k => k.category === cat).map(kpi => {
                const active = visibleIds.includes(kpi.id);
                return (
                  <div key={kpi.id}
                    onClick={() => onToggle(kpi.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 12px', borderRadius: 10,
                      border: `1.5px solid ${active ? '#4361ee' : '#e8ecf4'}`,
                      background: active ? '#eef2ff' : 'white',
                      cursor: 'pointer', transition: 'all .15s',
                    }}
                  >
                    <div style={{
                      width: 20, height: 20, borderRadius: 6,
                      border: `2px solid ${active ? '#4361ee' : '#cbd5e1'}`,
                      background: active ? '#4361ee' : 'white', flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {active && <span style={{ color: 'white', fontSize: 12, fontWeight: 700 }}>✓</span>}
                    </div>
                    <span style={{ fontSize: 15 }}>{kpi.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? '#4361ee' : '#374151', flex: 1 }}>
                      {kpi.label}
                    </span>
                    {active && <span style={{ fontSize: 10, color: '#4361ee', fontWeight: 700 }}>#{visibleIds.indexOf(kpi.id) + 1}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div style={{ padding: '16px 22px', borderTop: '1px solid #f0f4ff', display: 'flex', gap: 10, flexShrink: 0 }}>
        <button onClick={onReset} className="btn btn-secondary" style={{ fontSize: 13 }}>↩ Reset</button>
        <button onClick={onSave}  className="btn btn-primary"    style={{ flex: 1, fontSize: 13 }} disabled={saving}>
          {saving ? 'Saving...' : '💾 Save Layout'}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SECTION TITLE
// ─────────────────────────────────────────────────────────────────────────────
function SectionTitle({ children, link, linkTo }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>{children}</div>
      {link && (
        <Link to={linkTo} style={{
          fontSize: 11, color: '#4361ee', fontWeight: 600,
          background: '#eef2ff', padding: '4px 10px', borderRadius: 20,
          transition: 'background .15s',
        }}>
          {link} →
        </Link>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN DASHBOARD
// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { user } = useAuth();

  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(true);
  const [range, setRange]       = useState('30');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo,   setCustomTo]   = useState('');

  const [visibleIds, setVisibleIds]     = useState(DEFAULT_KPI_IDS);
  const [configOpen, setConfigOpen]     = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);

  // Drill-down modal state
  const [drill, setDrill] = useState(null); // { kpi, rows, columns, loading, error, summary }

  const dragItem = useRef(null);
  const dragOver = useRef(null);

  useEffect(() => {
    axios.get('/api/dashboard/config').then(r => {
      if (r.data.config?.visibleIds?.length > 0) setVisibleIds(r.data.config.visibleIds);
      setConfigLoaded(true);
    }).catch(() => setConfigLoaded(true));
  }, []);

  const loadData = useCallback((days, f, t) => {
    setLoading(true);
    const from = f || daysAgoIST(parseInt(days || 30));
    const to   = t || todayIST();
    axios.get(`/api/reports/summary?from=${from}&to=${to}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(range); }, []);

  const setRangeAndLoad = (d) => { setRange(d); setCustomFrom(''); setCustomTo(''); loadData(d); };
  const applyCustom = () => { if (customFrom && customTo) { setRange(''); loadData(null, customFrom, customTo); } };

  const handleToggle  = (id) => setVisibleIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const handleReorder = (newIds) => setVisibleIds(newIds);

  const handleGridDragStart = (id) => { dragItem.current = id; };
  const handleGridDragEnter = (id) => { dragOver.current = id; };
  const handleGridDragEnd   = () => {
    if (!dragItem.current || !dragOver.current || dragItem.current === dragOver.current) return;
    const list = [...visibleIds];
    const from = list.indexOf(dragItem.current);
    const to   = list.indexOf(dragOver.current);
    if (from < 0 || to < 0) return;
    list.splice(to, 0, list.splice(from, 1)[0]);
    setVisibleIds(list);
    dragItem.current = null;
    dragOver.current = null;
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await axios.put('/api/dashboard/config', { config: { visibleIds } });
      toast.success('Dashboard layout saved');
      setConfigOpen(false);
    } catch {
      toast.error('Failed to save layout');
    } finally {
      setSavingConfig(false);
    }
  };

  const resetConfig = async () => {
    setVisibleIds(DEFAULT_KPI_IDS);
    await axios.delete('/api/dashboard/config').catch(() => {});
    toast.success('Dashboard reset to defaults');
  };

  // ── Drill-down: open modal and fetch data ─────────────────
  const openDrill = async (kpi) => {
    if (!kpi.modal) return;
    const from = customFrom || daysAgoIST(parseInt(range || 30));
    const to   = customTo   || todayIST();
    setDrill({ kpi, rows: [], columns: [], loading: true, error: null, summary });
    try {
      const result = await kpi.modal.fetchFn(from, to);
      setDrill({ kpi, rows: result.rows, columns: result.columns, loading: false, error: null, summary });
    } catch (err) {
      const msg = err?.response?.data?.error || err.message || 'Failed to load data';
      setDrill(prev => ({ ...prev, loading: false, error: msg }));
    }
  };

  // ── Derived data ──────────────────────────────────────────
  const summary         = data?.summary        || {};
  const topGuards       = data?.topGuards       || [];
  const dailyStats      = data?.dailyStats      || [];
  const incidentsByDay  = data?.incidentsByDay  || [];
  const recentPatrols   = data?.recentPatrols   || [];
  const recentIncidents = data?.recentIncidents || [];

  const chartData = dailyStats.map(d => {
    const inc = incidentsByDay.find(i => i.date === d.date);
    return {
      date: fmtMd(d.date),
      patrols:        d.patrols,
      completed:      d.completed,
      avg_compliance: d.avg_compliance,
      incidents:      inc?.incidents || 0,
    };
  });

  const activeKpis = visibleIds.map(id => ALL_KPIS.find(k => k.id === id)).filter(Boolean);

  // greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? '🌅 Good morning' : hour < 17 ? '☀️ Good afternoon' : '🌙 Good evening';

  return (
    <div style={{ minHeight: '100vh' }}>

      {/* ── HEADER ───────────────────────────────────────────── */}
      <div className="dash-hero" style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)',
        padding: '22px 26px',
        marginBottom: 24,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 14,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative circles — desktop only */}
        <div className="dash-hero-deco" style={{
          position: 'absolute', top: -40, right: -40,
          width: 160, height: 160, borderRadius: '50%',
          background: 'rgba(67,97,238,0.12)', pointerEvents: 'none',
        }} />
        <div className="dash-hero-deco" style={{
          position: 'absolute', bottom: -30, right: 120,
          width: 100, height: 100, borderRadius: '50%',
          background: 'rgba(139,92,246,0.1)', pointerEvents: 'none',
        }} />

        {/* Greeting */}
        <div style={{ position: 'relative' }}>
          <div className="dash-greeting" style={{ fontSize: 22, fontWeight: 800, color: 'white', letterSpacing: -.3 }}>
            {greeting}, {user?.name?.split(' ')[0]}!
          </div>
          <div className="dash-date" style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>
            {fmtFull(new Date())} &nbsp;·&nbsp; Security Operations Center
          </div>
        </div>

        {/* Controls */}
        <div className="dash-header-controls" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', position: 'relative' }}>
          {/* Range buttons */}
          <div className="dash-range-btns" style={{
            display: 'flex', gap: 4,
            background: 'rgba(255,255,255,0.08)',
            borderRadius: 10, padding: 4,
          }}>
            {['7', '14', '30', '90'].map(d => (
              <button key={d} onClick={() => setRangeAndLoad(d)}
                style={{
                  padding: '6px 14px', borderRadius: 7, border: 'none',
                  background: range === d ? '#4361ee' : 'transparent',
                  color: range === d ? 'white' : '#cbd5e1',
                  fontWeight: range === d ? 700 : 500,
                  fontSize: 13, cursor: 'pointer', transition: 'all .15s',
                  minWidth: 44,
                }}>
                {d}d
              </button>
            ))}
          </div>

          {/* Custom date range — hidden on mobile */}
          <div className="dash-date-range" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ width: 1, height: 24, background: 'rgba(255,255,255,0.15)', margin: '0 2px' }} />
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
              style={{
                padding: '5px 10px', borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.08)',
                color: 'white', fontSize: 12, outline: 'none',
              }} />
            <span style={{ color: '#64748b', fontSize: 12 }}>–</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
              style={{
                padding: '5px 10px', borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.15)',
                background: 'rgba(255,255,255,0.08)',
                color: 'white', fontSize: 12, outline: 'none',
              }} />
            <button onClick={applyCustom} disabled={!customFrom || !customTo}
              style={{
                padding: '5px 14px', borderRadius: 8, border: 'none',
                background: '#4361ee', color: 'white', fontSize: 12,
                fontWeight: 600, cursor: customFrom && customTo ? 'pointer' : 'not-allowed',
                opacity: customFrom && customTo ? 1 : 0.5, transition: 'opacity .15s',
              }}>Apply</button>
          </div>

          {/* Customize button */}
          <button onClick={() => setConfigOpen(true)}
            style={{
              padding: '7px 16px', borderRadius: 10,
              border: '1px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.1)',
              color: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 600,
              display: 'flex', alignItems: 'center', gap: 6,
              backdropFilter: 'blur(4px)', transition: 'background .15s',
              WebkitTapHighlightColor: 'transparent',
            }}
            title="Customize KPI cards">
            ⚙️ <span className="dash-customize-label">Customize</span>
          </button>
        </div>
      </div>

      {/* ── LOADING ────────────────────────────────────────── */}
      {loading && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '60px 0', color: '#94a3b8',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'linear-gradient(135deg,#4361ee,#8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, marginBottom: 14,
            boxShadow: '0 8px 24px rgba(67,97,238,0.3)',
          }}>📊</div>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#1e293b' }}>Loading dashboard…</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>Fetching your patrol data</div>
        </div>
      )}

      {!loading && (
        <>
          {/* ── KPI GRID ─────────────────────────────────────── */}
          {activeKpis.length === 0 ? (
            <div style={{
              background: 'white', borderRadius: 16,
              border: '2px dashed #e2e8f0',
              padding: '56px 24px', textAlign: 'center', marginBottom: 24,
              boxShadow: '0 2px 8px rgba(0,0,0,.04)',
            }}>
              <div style={{ fontSize: 40, marginBottom: 14 }}>📊</div>
              <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8, color: '#1e293b' }}>No KPI cards selected</div>
              <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 18 }}>
                Click Customize to add cards to your dashboard
              </div>
              <button onClick={() => setConfigOpen(true)} className="btn btn-primary">
                ⚙️ Customize Dashboard
              </button>
            </div>
          ) : (
            <div className="dash-kpi-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(175px, 1fr))',
              gap: 14,
              marginBottom: 26,
            }}>
              {activeKpis.map(kpi => (
                <div
                  key={kpi.id}
                  draggable
                  onDragStart={() => handleGridDragStart(kpi.id)}
                  onDragEnter={() => handleGridDragEnter(kpi.id)}
                  onDragEnd={handleGridDragEnd}
                  onDragOver={e => e.preventDefault()}
                >
                  <KpiCard
                    kpi={kpi}
                    summary={summary}
                    dragHandleProps={{}}
                    onClick={() => openDrill(kpi)}
                  />
                </div>
              ))}
            </div>
          )}

          {/* ── CHARTS ────────────────────────────────────────── */}
          <div className="dash-charts-grid">

            {/* Area chart – Activity & Compliance */}
            <div style={{
              background: 'white', borderRadius: 16, padding: '20px 20px 14px',
              boxShadow: '0 2px 12px rgba(0,0,0,.06)', border: '1px solid #e8ecf4',
            }}>
              <SectionTitle>📈 Activity & Compliance</SectionTitle>
              {chartData.length === 0 ? <NoData /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="gradPatrols" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#4361ee" stopOpacity={0.2} />
                        <stop offset="95%" stopColor="#4361ee" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gradCompliance" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="#22c55e" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="l" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    <Area yAxisId="l" type="monotone" dataKey="patrols" stroke="#4361ee" strokeWidth={2.5} fill="url(#gradPatrols)" dot={false} name="Patrols" />
                    <Area yAxisId="r" type="monotone" dataKey="avg_compliance" stroke="#22c55e" strokeWidth={2.5} fill="url(#gradCompliance)" dot={false} name="Compliance %" />
                    <Line yAxisId="l" type="monotone" dataKey="incidents" stroke="#ef233c" strokeWidth={2} dot={false} name="Incidents" strokeDasharray="4 3" />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Bar chart – Completed vs Total */}
            <div style={{
              background: 'white', borderRadius: 16, padding: '20px 20px 14px',
              boxShadow: '0 2px 12px rgba(0,0,0,.06)', border: '1px solid #e8ecf4',
            }}>
              <SectionTitle>📊 Completed vs Total</SectionTitle>
              {chartData.length === 0 ? <NoData /> : (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={chartData} margin={{ top: 4, right: 4, left: -18, bottom: 0 }} barGap={3}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    <Bar dataKey="patrols"   fill="#c7d2fe" name="Total"     radius={[5, 5, 0, 0]} />
                    <Bar dataKey="completed" fill="#4361ee" name="Completed" radius={[5, 5, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* ── BOTTOM ROW ────────────────────────────────────── */}
          <div className="dash-bottom-grid">

            {/* Recent Patrols */}
            <div style={{
              background: 'white', borderRadius: 16, padding: '20px',
              boxShadow: '0 2px 12px rgba(0,0,0,.06)', border: '1px solid #e8ecf4',
            }}>
              <SectionTitle link="View all" linkTo="/patrols">🚶 Recent Patrols</SectionTitle>
              <div className="table-scroll">
                <table className="table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Guard</th><th>Route</th><th>Site</th>
                      <th>Status</th><th>CPs</th><th>Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentPatrols.length === 0
                      ? <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: 28 }}>No patrols yet</td></tr>
                      : recentPatrols.map(p => {
                          const c = parseFloat(p.compliance_percent);
                          const statusStyle = {
                            active:    { bg: '#fef3c7', color: '#92400e' },
                            completed: { bg: '#dcfce7', color: '#166534' },
                          }[p.status] || { bg: '#fee2e2', color: '#991b1b' };
                          return (
                            <tr key={p.id}>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                  <div style={{
                                    width: 28, height: 28, borderRadius: 8,
                                    background: 'linear-gradient(135deg,#4361ee,#8b5cf6)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontSize: 13, flexShrink: 0,
                                  }}>👮</div>
                                  <span style={{ fontWeight: 600, color: '#1e293b' }}>{p.guard_name}</span>
                                </div>
                              </td>
                              <td style={{ color: '#475569', fontWeight: 500 }}>{p.route_name}</td>
                              <td style={{ color: '#94a3b8' }}>{p.site_name}</td>
                              <td>
                                <span style={{
                                  fontSize: 10, padding: '3px 10px', borderRadius: 20, fontWeight: 700,
                                  background: statusStyle.bg, color: statusStyle.color,
                                  textTransform: 'capitalize',
                                }}>{p.status}</span>
                              </td>
                              <td>
                                <span style={{ color: '#1e293b', fontWeight: 600 }}>{p.completed_checkpoints}</span>
                                <span style={{ color: '#94a3b8' }}>/{p.total_checkpoints}</span>
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <div style={{
                                    flex: 1, height: 5, borderRadius: 3,
                                    background: '#f1f5f9', overflow: 'hidden', minWidth: 40,
                                  }}>
                                    <div style={{
                                      width: `${Math.min(c, 100)}%`, height: '100%',
                                      background: c >= 80 ? '#22c55e' : c >= 60 ? '#f59e0b' : '#ef233c',
                                      borderRadius: 3, transition: 'width .3s',
                                    }} />
                                  </div>
                                  <span style={{
                                    fontWeight: 700, fontSize: 12, minWidth: 36,
                                    color: c >= 80 ? '#16a34a' : c >= 60 ? '#d97706' : '#dc2626',
                                  }}>{c}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                    }
                  </tbody>
                </table>
              </div>
            </div>

            {/* Right column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

              {/* Recent Incidents */}
              <div style={{
                background: 'white', borderRadius: 16, padding: '18px 18px 14px',
                boxShadow: '0 2px 12px rgba(0,0,0,.06)', border: '1px solid #e8ecf4', flex: 1,
              }}>
                <SectionTitle link="All" linkTo="/incidents">⚠️ Recent Incidents</SectionTitle>
                {recentIncidents.length === 0
                  ? <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No incidents reported</div>
                  : recentIncidents.map(inc => (
                      <div key={inc.id} style={{
                        display: 'flex', gap: 10, padding: '9px 0',
                        borderBottom: '1px solid #f8fafc', alignItems: 'flex-start',
                      }}>
                        <div style={{
                          width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                          background: (SEV[inc.severity] || '#9ca3af') + '18',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 14,
                        }}>
                          {inc.severity === 'critical' ? '🚨' : inc.severity === 'high' ? '🔴' : inc.severity === 'medium' ? '🟠' : '🟡'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {inc.title}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                            {inc.guard_name} · {fmtMdHm(inc.reported_at)}
                          </div>
                        </div>
                        <span style={{
                          fontSize: 10, padding: '3px 8px', borderRadius: 20, fontWeight: 700,
                          background: (SEV[inc.severity] || '#9ca3af') + '20',
                          color: SEV[inc.severity] || '#9ca3af',
                          textTransform: 'capitalize', whiteSpace: 'nowrap',
                          alignSelf: 'center',
                        }}>
                          {inc.severity}
                        </span>
                      </div>
                    ))
                }
              </div>

              {/* Guard Leaderboard */}
              <div style={{
                background: 'white', borderRadius: 16, padding: '18px 18px 14px',
                boxShadow: '0 2px 12px rgba(0,0,0,.06)', border: '1px solid #e8ecf4',
              }}>
                <SectionTitle link="All" linkTo="/guards">🏆 Guard Leaderboard</SectionTitle>
                {topGuards.length === 0
                  ? <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '16px 0' }}>No completed patrols</div>
                  : topGuards.slice(0, 5).map((g, i) => {
                      const medals = ['🥇', '🥈', '🥉'];
                      const rankBg = i === 0
                        ? 'linear-gradient(135deg,#f59e0b,#fcd34d)'
                        : i === 1
                        ? 'linear-gradient(135deg,#94a3b8,#cbd5e1)'
                        : i === 2
                        ? 'linear-gradient(135deg,#b45309,#d97706)'
                        : 'linear-gradient(135deg,#e2e8f0,#f1f5f9)';
                      return (
                        <div key={g.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 0', borderBottom: '1px solid #f8fafc',
                        }}>
                          <div style={{
                            width: 26, height: 26, borderRadius: 8, flexShrink: 0,
                            background: rankBg,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: i < 3 ? 14 : 11, fontWeight: 800,
                            color: i < 3 ? undefined : '#64748b',
                            boxShadow: i < 3 ? '0 2px 6px rgba(0,0,0,.15)' : 'none',
                          }}>
                            {i < 3 ? medals[i] : i + 1}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {g.name}
                            </div>
                            <div style={{ fontSize: 11, color: '#94a3b8' }}>{g.patrol_count} patrols</div>
                          </div>
                          {/* Compliance pill */}
                          <div style={{
                            padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 800,
                            background: g.avg_compliance >= 80 ? '#dcfce7' : '#fef3c7',
                            color: g.avg_compliance >= 80 ? '#16a34a' : '#d97706',
                          }}>
                            {g.avg_compliance}%
                          </div>
                        </div>
                      );
                    })
                }
              </div>

            </div>
          </div>
        </>
      )}

      {/* ── CONFIG PANEL ─────────────────────────────────────── */}
      {configOpen && (
        <>
          <div onClick={() => setConfigOpen(false)}
            style={{
              position: 'fixed', inset: 0,
              background: 'rgba(15,20,50,0.45)',
              backdropFilter: 'blur(4px)',
              zIndex: 999,
            }} />
          <ConfigPanel
            visibleIds={visibleIds}
            onToggle={handleToggle}
            onReorder={handleReorder}
            onClose={() => setConfigOpen(false)}
            onReset={resetConfig}
            onSave={saveConfig}
            saving={savingConfig}
          />
        </>
      )}

      {/* ── KPI DRILL-DOWN MODAL ──────────────────────────────── */}
      {drill && (
        <KpiDrillModal
          drill={drill}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
}

function NoData() {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', height: 220, color: '#94a3b8', gap: 8,
    }}>
      <div style={{ fontSize: 28 }}>📭</div>
      <div style={{ fontSize: 13, fontWeight: 500 }}>No data in selected range</div>
    </div>
  );
}
